import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

interface RevertSessionConsumptionParams {
  bookingId?: string;
  consumptionId?: string;
}

export const useRevertSessionConsumption = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();
  const { t } = useTranslation();
  
  return useMutation({
    mutationFn: async ({ bookingId, consumptionId }: RevertSessionConsumptionParams) => {
      console.log('Reverting session consumption:', { bookingId, consumptionId });
      
      // First, find the session consumption record
      let sessionConsumption;
      
      if (consumptionId) {
        const { data, error } = await supabase
          .from('session_consumptions')
          .select('*')
          .eq('id', consumptionId)
          .single();
          
        if (error) {
          console.error('Error finding session consumption by ID:', error);
          throw new Error(error.message);
        }
        sessionConsumption = data;
      } else if (bookingId) {
        const { data, error } = await supabase
          .from('session_consumptions')
          .select('*')
          .eq('booking_id', bookingId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
          
        if (error) {
          console.error('Error finding session consumption by booking ID:', error);
          throw new Error(error.message);
        }
        sessionConsumption = data;
      } else {
        throw new Error('Either bookingId or consumptionId must be provided');
      }

      if (!sessionConsumption) {
        throw new Error('Session consumption record not found');
      }

      // Delete the session consumption record
      const { error: deleteError } = await supabase
        .from('session_consumptions')
        .delete()
        .eq('id', sessionConsumption.id);

      if (deleteError) {
        console.error('Error deleting session consumption:', deleteError);
        throw new Error(deleteError.message);
      }

      // Get current subscription data and decrease sessions used by 1
      const { data: subscription, error: fetchError } = await supabase
        .from('client_subscriptions')
        .select('current_cycle_sessions_used')
        .eq('id', sessionConsumption.subscription_id)
        .single();

      if (fetchError || !subscription) {
        console.error('Error fetching subscription:', fetchError);
        throw new Error('Failed to fetch subscription data');
      }

      const { error: updateError } = await supabase
        .from('client_subscriptions')
        .update({ 
          current_cycle_sessions_used: Math.max(0, subscription.current_cycle_sessions_used - 1)
        })
        .eq('id', sessionConsumption.subscription_id);

      if (updateError) {
        console.error('Error updating subscription sessions:', updateError);
        // We still want to continue since the consumption was deleted
      }

      // If there's a booking_id, reset the booking status to confirmed
      if (sessionConsumption.booking_id) {
        const { error: bookingError } = await supabase
          .from('bookings')
          .update({ status: 'confirmed' })
          .eq('id', sessionConsumption.booking_id);

        if (bookingError) {
          console.error('Error resetting booking status:', bookingError);
          // Continue since the main action (deleting consumption) succeeded
        }
      }

      console.log('Session consumption reverted successfully');
      return sessionConsumption;
    },
    onSuccess: () => {
      console.log('Session consumption revert mutation succeeded');
      toast.success(t('sessions.revertSuccess', { defaultValue: 'Session reverted successfully' }));
      
      // Invalidate relevant queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['clients', currentOrganization?.id] });
      queryClient.invalidateQueries({ queryKey: ['bookings', currentOrganization?.id] });
      queryClient.invalidateQueries({ queryKey: ['effective-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['session-history'] });
    },
    onError: (error) => {
      console.error('Session consumption revert mutation failed:', error);
      toast.error(t('sessions.revertFailed', { defaultValue: 'Failed to revert session' }));
    }
  });
};